import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './i18n';
import AuthContent from './components/AuthContent';
import OfflineIndicator from './components/OfflineIndicator';
import ConsentModal from './components/auth/ConsentModal';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import PublicCardSubmission from './components/public/PublicCardSubmission';
import AssetDashboard from './components/dashboard/AssetDashboard';
import './App.css';

// Global styles for task groups
const globalStyles = `
  .task-group-root {
    margin-left: 0;
  }

  .task-group-nested {
    margin-left: 0;
  }

  .task-group-content {
    padding: 0.5rem;  /* No padding on mobile - aligns with card edge */
  }

  @media (min-width: 768px) {
    .task-group-nested {
      margin-left: 1rem;
    }

    .task-group-content {
      padding: 1.25rem;  /* Normal padding on desktop */
    }
  }
`;

// ============================================
// MAIN APP
// ============================================

// Public Dashboard Route component - extracts token from URL
function PublicDashboardRoute() {
  const { token } = useParams();
  return <AssetDashboard publicToken={token} />;
}

// Main App Content with routing
function AppContent({ showRegister, setShowRegister }) {
  const { needsConsent, onConsentComplete, logout, user } = useAuth();

  return (
    <>
      <style>{globalStyles}</style>
      <OfflineIndicator />

      {/* Show consent modal if user is logged in but hasn't given consents */}
      {user && needsConsent && (
        <ConsentModal
          onComplete={onConsentComplete}
          onLogout={logout}
        />
      )}

      <AuthContent
        showRegister={showRegister}
        setShowRegister={setShowRegister}
      />
    </>
  );
}

export default function App() {
  const [showRegister, setShowRegister] = useState(false);

  return (
    <BrowserRouter basename="/taskmanager">
      <Routes>
        {/* Password reset pages - no auth required */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Public card submission - no auth required */}
        <Route path="/submit-card" element={<PublicCardSubmission />} />

        {/* Public dashboard - no auth required (token-based) */}
        <Route
          path="/dashboard/:token"
          element={<PublicDashboardRoute />}
        />

        {/* Main app */}
        <Route
          path="/*"
          element={
            <LanguageProvider>
              <AuthProvider>
                <AppContent
                  showRegister={showRegister}
                  setShowRegister={setShowRegister}
                />
              </AuthProvider>
            </LanguageProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
